import React, { FC, useEffect, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import styled from 'styled-components';
import classNames from 'classnames';
import CloseIcon from '@mui/icons-material/Close';

import { useMountedEffect } from '@clatter/platform';
import searchIcon from '../../../../assets/icons/search.svg';

const StyledSearchInput = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  box-sizing: border-box;
  max-width: 100%;
  border: 1px solid ${({ theme }) => theme?.palette?.border};
  border-radius: ${({ theme }) => theme?.radius?.small};
  background-color: ${({ theme }) => theme?.palette?.white};
  align-items: center;
  justify-content: center;

  svg {
    transform: translate(13px, 5px);
    position: absolute;
    top: 3px;
    left: 0px;
  }

  &.error {
    border-color: ${({ theme }) => theme?.palette?.error} !important;
  }
`;

const StyledInput = styled.input<{ $hideIcon: boolean }>`
  display: block;
  line-height: 20px;
  /* padding: ${({ $hideIcon }) => ($hideIcon ? '0 42px 0 12px' : '0 12px 0 45px')}; */
  padding: 0 36px 0 12px;
  border: none;
  outline: none;
  margin: 0;
  background-color: transparent;
  box-sizing: border-box;
  height: 2.35rem;
  width: 100%;
  color: ${({ theme }) => theme?.palette?.text};

  &::placeholder {
    color: ${({ theme }) => theme?.palette?.placeholderText};
  }
`;

const StyledSearchIcon = styled.img.attrs({ src: searchIcon, alt: 'Search' })`
  stroke: ${({ theme }) => theme?.palette?.gray};
  transform: scaleX(-1);
`;

const StyledCloseButton = styled.div`
  cursor: pointer;
  position: absolute;
  right: 40px;
  top: 2px;

  svg {
    width: 16px;
    height: 16px;
    fill: ${({ theme }) => theme?.palette?.gray};
  }

  &.disabled {
    pointer-events: none;
  }
`;

interface SearchInputProps {
  value?: string;
  timeout?: number;
  placeholder?: string;
  hideIcon?: boolean;
  hideClear?: boolean;
  noDebounce?: boolean;
  hasError?: boolean;
  shouldFocus?: boolean;
  disabled?: boolean;
  onSearch: (value: string) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const SearchInput: FC<SearchInputProps> = ({
  value,
  placeholder = 'Search...',
  onSearch,
  hideIcon = false,
  hideClear = false,
  noDebounce = false,
  shouldFocus = false,
  hasError = false,
  timeout = 500,
  disabled,
  onKeyDown,
}) => {
  const isControlled = value !== undefined;

  const [localValue, setLocalValue] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedDispatch = useRef(debounce(onSearch, timeout)).current;

  const showClearButton = isControlled ? (value ?? '').length > 0 : localValue.length > 0;

  const handleReset = () => {
    if (!isControlled) setLocalValue('');
    onSearch('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVal = e.target.value;
    if (!isControlled) setLocalValue(newVal);

    if (isControlled || noDebounce) {
      // If controlled, call immediately to avoid lag
      onSearch(newVal);
    } else {
      // Uncontrolled with debounce
      debouncedDispatch(newVal);
    }
  };

  useEffect(() => {
    if (isControlled) setLocalValue(value ?? '');
  }, [value, isControlled]);

  useMountedEffect(() => {
    if (shouldFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [shouldFocus]);

  useEffect(() => () => debouncedDispatch.cancel(), [debouncedDispatch]);

  return (
    <StyledSearchInput className={classNames('input-container', { error: hasError })}>
      {!hideIcon && <StyledSearchIcon />}
      <StyledInput
        ref={inputRef}
        type="text"
        value={isControlled ? value ?? '' : localValue}
        onChange={handleChange}
        disabled={disabled}
        placeholder={placeholder}
        $hideIcon={hideIcon}
        onKeyDown={onKeyDown}
      />
      {!hideClear && showClearButton && (
        <StyledCloseButton className={classNames({ disabled: disabled })} onClick={handleReset}>
          <CloseIcon />
        </StyledCloseButton>
      )}
    </StyledSearchInput>
  );
};
